import React, { Component } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { connect } from "react-redux";
import ReactPlayer from "react-player";
import { TldrAnimationPlaybackAction } from "./statelessView";
import {
  black,
  grey,
  lightInactive,
  primary,
  primary_00,
  primayColor,
} from "../styled/variable";
import styled from "styled-components";
import { Rnd } from "react-rnd";
import { TIMELINE_MINIMUM_PAGE_DURATION } from "../../_utils/constants";
import {
  FaCut,
  FaGripLinesVertical,
  FaPause,
  FaPlay,
  FaVolumeMute,
  FaVolumeUp,
} from "react-icons/fa";
import {
  Button,
  ButtonGroup,
  Switch,
  Tooltip,
  Modal,
  ModalCloseButton,
  ModalBody,
  ModalContent,
  ModalOverlay,
  ModalHeader,
  VStack,
  Flex,
  ModalFooter,
  Text,
  Divider,
} from "@chakra-ui/react";
import { withTranslation } from "react-i18next";

dayjs.extend(duration);

const StyledVideoTrackContainer = styled.div`
  width: 100%;
  height: 50px;
  background-color: ${grey};
  margin: 0 8px;
  border-radius: 8px;
  position: relative;
  overflow: hidden;

  .cursor {
    position: absolute;
    top: 0;
    width: 2px;
    background-color: white;
    height: 100%;

    transition: left 0.2s;
  }
`;

const StyledTrimWindow = styled(Rnd)`
  height: 100%;
  border: 2px solid ${primary_00};
  border-radius: 8px;
  overflow: hidden;

  .handle {
    width: 12px;
    background-color: ${primary_00};
    height: 100%;
    color: ${black};
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .handle-left {
    left: 0;
  }

  .handle-right {
    right: 0;
  }
`;

export const StyledVideoProgressRow = styled.div`
  display: flex;
  flex-direction: row;
  margin: 1rem 0rem 1rem 0rem;
  width: 100%;
  justify-content: space-between;
  align-items: center;

  .actions {
    .playback-button {
      background-color: ${primary} !important;
      color: ${primayColor};

      :hover:not([disabled]) {
        color: ${primayColor} !important;
        outline: none !important;
      }
    }
  }
`;

const Duration = ({ seconds, showInSeconds }) => {
  return (
    <span>
      {showInSeconds
        ? `${Number(seconds).toFixed(1)}s`
        : dayjs
            .duration(seconds, "seconds")
            .format(showInSeconds ? "s" : "mm:ss")}
    </span>
  );
};

class TldrMediaTrimmingModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      x: 0, // In the pixels
      width: 0, // In the pixels
      trimToArtboardDuration: false,

      // Media player controls
      isPlaying: false,
      currentTime: 0,
      isMuted: true,
      duration: null,
      isLoading: true,
    };
    this.player = React.createRef();
    this.trimVideoTrack = React.createRef();
  }

  onDuration = (duration) => {
    duration = Math.floor(duration * 10) / 10;

    const [x, width] = this.getGrabberSize(duration);
    this.setState(
      {
        x,
        width,
        duration,
        isLoading: false,
      },
      () => {
        this.seekTo(this.startTime);
      }
    );
  };

  getGrabberSize = (duration = null) => {
    const { data } = this.props;
    const startTime = data?.payload?.startTime ?? 0;
    const endTime = data?.payload?.endTime ?? duration;
    const trimVideoTrack = this.trimVideoTrack.current;
    if (trimVideoTrack && duration) {
      return [
        (startTime * trimVideoTrack.offsetWidth) / duration, // X offset value in px
        ((endTime - startTime) * trimVideoTrack.offsetWidth) / duration, // Width value in px
      ];
    }
    return [null, null];
  };

  componentDidMount() {
    const { data } = this.props;
    const duration = data?.payload?.duration ?? data?.content?.meta?.duration;
    const [x, width] = this.getGrabberSize(duration);

    const startTime = data?.payload?.startTime ?? 0;
    if (duration) {
      this.setState(
        {
          x: x,
          width: width,
          duration,
          currentTime: startTime,
          isMuted: data?.payload?.muted ?? true,
        },
        () => {
          this.seekTo(startTime);
        }
      );
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // If video data is changed
    if (this.props.data !== prevProps.data) {
      const videoData = this.props.data;
      const duration =
        videoData?.payload?.duration ?? videoData?.content?.meta?.duration;

      const startTime = videoData?.payload?.startTime ?? 0;
      const [x, width] = this.getGrabberSize(duration);
      this.setState(
        {
          duration,
          x,
          width,
          currentTime: startTime,
          isMuted: videoData?.payload?.muted,
        },
        () => {
          this.seekTo(startTime);
        }
      );
    }
  }

  onProgress = (timeBlock) => {
    if (timeBlock.playedSeconds < this.endTime) {
      this.setState({
        currentTime: timeBlock.playedSeconds,
      });
    } else {
      this.seekTo(this.startTime);
      this.setState({
        currentTime: this.startTime,
        isPlaying: false,
      });
    }
  };

  handlePlayPause = () => {
    this.setState((prevState) => ({
      isPlaying: !prevState.isPlaying,
    }));
  };

  get videoTrackWidth() {
    return this.trimVideoTrack.current?.offsetWidth ?? 0;
  }

  get trimmedDuration() {
    const { width, duration } = this.state;
    if (this.videoTrackWidth) {
      return (duration * width) / this.videoTrackWidth;
    }
    return 0;
  }

  seekTo = (time) => {
    if (this.player.current && !this.state.isLoading) {
      this.player.current.seekTo(time, "seconds");
    }
  };

  toggleTrimToArtboard = (shouldTrimToArtboard) => {
    if (shouldTrimToArtboard) {
      const {
        editor: {
          activePage: { id: activePageId },
        },
        pages,
        data,
      } = this.props;

      const { duration, x } = this.state;
      const availableTrimArea = this.videoTrackWidth - x;

      const activePageData = pages[activePageId];
      const pageDuration = activePageData?.payload?.animation?.duration ?? 6;
      const trimDuration = Math.min(duration, pageDuration);
      const trimWidth = (trimDuration / duration) * this.videoTrackWidth;
      this.setState({
        width: Math.min(trimWidth, availableTrimArea),
        trimToArtboardDuration: !this.state.trimToArtboardDuration,
      });
    } else {
      this.setState({
        trimToArtboardDuration: !this.state.trimToArtboardDuration,
      });
    }
  };

  toggleMute = () => {
    this.setState({ isMuted: !this.state.isMuted });
  };

  onResize = (e, direction, ref, delta, pos) => {
    this.setState({
      x: pos.x,
      width: ref.offsetWidth,
    });
  };

  onResizeStop = (e, direction, ref, delta, pos) => {
    this.setState(
      {
        x: pos.x,
        width: ref.offsetWidth,
      },
      () => {
        this.seekTo(this.startTime);
      }
    );
  };

  onDrag = (e, { x, node }) => {
    this.setState({
      x,
      width: node.offsetWidth,
    });
  };

  onDragStop = (e, { x, node }) => {
    this.setState(
      {
        x,
        width: node.offsetWidth,
      },
      () => {
        this.seekTo(this.startTime);
      }
    );
  };

  get startTime() {
    const { x, duration } = this.state;
    return this.videoTrackWidth
      ? Number(Number((x / this.videoTrackWidth) * duration).toFixed(1))
      : 0;
  }

  get endTime() {
    const { x, duration, width } = this.state;
    return this.videoTrackWidth
      ? Number(
          Number(((x + width) / this.videoTrackWidth) * duration).toFixed(1)
        )
      : 0;
  }

  onTrim = () => {
    this.props.onSubmit({
      startTime: this.startTime,
      endTime: this.endTime,
      muted: this.state.isMuted,
    });
  };

  render() {
    const {
      show,
      onHide,
      data: {
        payload: { src },
      },
      t,
    } = this.props;
    const {
      isPlaying,
      currentTime,
      isMuted,
      duration,
      trimToArtboardDuration,
      x,
      width,
    } = this.state;
    const showDurationInSeconds = duration <= 60;

    let minimumWidth = undefined;
    if (this.videoTrackWidth) {
      minimumWidth =
        (TIMELINE_MINIMUM_PAGE_DURATION * this.videoTrackWidth) / duration;
    }

    return (
      <Modal isOpen={show} onClose={onHide} isCentered size="3xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <VStack align="flex-start">
              <Text>{t("trim_video")}</Text>
              <Text
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: "400",
                  color: "gray.300",
                  marginTop: "0 !important",
                }}
              >
                {t("media_trim_msg")}
              </Text>
            </VStack>
          </ModalHeader>
          <Divider my="1" />
          <ModalCloseButton />

          <ModalBody>
            <Flex direction="column" align="center" px="1rem" py="1rem">
              <Flex direction="column" align="center" justify="center">
                <ReactPlayer
                  progressInterval={100}
                  url={src}
                  ref={this.player}
                  playing={isPlaying}
                  loop={false}
                  muted={isMuted}
                  onProgress={this.onProgress}
                  onDuration={this.onDuration}
                  width="50vw"
                  height="auto"
                  style={{
                    maxWidth: "600px",
                  }}
                />
                <Flex
                  direction="row"
                  mt="0.25rem"
                  w="100%"
                  justify="space-between"
                >
                  <Duration seconds={0} showInSeconds={showDurationInSeconds} />
                  <Duration
                    seconds={duration}
                    showInSeconds={showDurationInSeconds}
                  />
                </Flex>
              </Flex>

              <Flex alignSelf="start" my="0.5rem" align="center">
                <Switch
                  onChange={this.toggleTrimToArtboard}
                  isChecked={trimToArtboardDuration}
                />

                <label
                  htmlFor="trim-checkbox"
                  style={{
                    fontSize: "14px",
                    color: lightInactive,
                    marginLeft: "8px",
                    marginBottom: 0,
                  }}
                >
                  {t("trim_artboard_duration")}
                </label>
              </Flex>

              <StyledVideoProgressRow>
                <div className="actions">
                  <TldrAnimationPlaybackAction
                    className="playback-button"
                    action={isPlaying ? "pause" : "play"}
                    icon={isPlaying ? <FaPause /> : <FaPlay />}
                    title={
                      isPlaying
                        ? t("pause", { ns: "common" })
                        : t("play", { ns: "common" })
                    }
                    callback={this.handlePlayPause}
                  />
                </div>
                <StyledVideoTrackContainer
                  ref={this.trimVideoTrack}
                  id="trim-video-track"
                >
                  <div
                    className="cursor"
                    style={{ left: `${(currentTime / duration) * 100}%` }}
                  />
                  <StyledTrimWindow
                    ref={this.rndRef}
                    bounds="#trim-video-track"
                    onResize={this.onResize}
                    onDrag={this.onDrag}
                    onResizeStop={this.onResizeStop}
                    onDragStop={this.onDragStop}
                    minWidth={minimumWidth}
                    default={{
                      x: x,
                      y: 0,
                      width: `${width}`,
                      height: "100%",
                    }}
                    position={{ x, y: 0 }}
                    size={{ width: `${width}`, height: "100%" }}
                    enableResizing={{
                      left: !trimToArtboardDuration,
                      right: !trimToArtboardDuration,
                    }}
                    resizeHandleStyles={{
                      left: { left: 0 },
                      right: { right: 0 },
                    }}
                    resizeHandleComponent={{
                      left: (
                        <Tooltip
                          hasArrow
                          placement="top"
                          label={
                            <Duration
                              seconds={this.startTime}
                              showInSeconds={showDurationInSeconds}
                            />
                          }
                        >
                          <div className="handle">
                            <FaGripLinesVertical />
                          </div>
                        </Tooltip>
                      ),
                      right: (
                        <Tooltip
                          hasArrow
                          placement="top"
                          label={
                            <Duration
                              seconds={this.endTime}
                              showInSeconds={showDurationInSeconds}
                            />
                          }
                        >
                          <div className="handle">
                            <FaGripLinesVertical />
                          </div>
                        </Tooltip>
                      ),
                    }}
                  />
                </StyledVideoTrackContainer>

                <div className="actions">
                  <TldrAnimationPlaybackAction
                    action={isMuted ? "volume-mute" : "volume-up"}
                    icon={isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                    title={
                      isMuted
                        ? t("unmute", { ns: "common" })
                        : t("mute", { ns: "common" })
                    }
                    callback={this.toggleMute}
                  />
                </div>
              </StyledVideoProgressRow>

              <div>
                <Duration
                  seconds={this.trimmedDuration}
                  showInSeconds={showDurationInSeconds}
                />{" "}
                {t("selected", { ns: "common" })}
              </div>
            </Flex>
          </ModalBody>

          <Divider my="1" />
          <ModalFooter>
            <ButtonGroup>
              <Button
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  onHide();
                }}
              >
                {t("cancel", { ns: "common" })}
              </Button>
              <Button
                variant="solid"
                onClick={(e) => {
                  e.stopPropagation();
                  this.onTrim();
                }}
                leftIcon={<FaCut />}
              >
                {t("trim")}
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }
}

TldrMediaTrimmingModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  data: PropTypes.object,
};

const mapStateToProps = (state) => ({
  editor: state.editor,
  pages: state.pagestore.pages,
});

const mapDispatchToProps = (dispatch) => ({});

export default withTranslation(["studio"])(
  connect(mapStateToProps, mapDispatchToProps)(TldrMediaTrimmingModal)
);
